<template>
  <div class="other-chat-bubble">
    <div class="other-chat-bubble__content">
      <GeneralMessageChatComingTextSource
        v-show="isVisibleSource"
        :is-complete="message.src.isComplete"
        :is-source-playing
        :chat-contents="message.src.contents"
        :is-pending-src="isPendingSrc"
        :language-code-set="message.srcLanguageSet"
        :using-auto-height="usingAutoHeight"
      />
      <div v-show="isVisibleSource && isVisibleTarget" class="wrapper-divider">
        <div class="divider" />
        <div class="actions">
          <GeneralMessageChatComingActions
            is-visible-button-copy
            :is-downloading="isDownloadingTarget"
            :is-playing-audio="isTargetPlaying"
            :is-top="false"
            :is-last-is-other="isLastIsOther"
            :is-target="true"
            :message="message"
            :play-audio="playTargetAudio"
          />
        </div>
      </div>
      <GeneralMessageChatComingTextTarget
        v-show="isVisibleTarget"
        :is-complete="message.tgt.isComplete"
        :is-target-playing
        :chat-contents="message.tgt.contents"
        :is-interpreting="isInterpretingTgt"
        :language-code-set="message.tgtLanguageSet"
        :using-auto-height="usingAutoHeight"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, ref, watch } from 'vue';

import { storeToRefs } from 'pinia';

import GeneralMessageChatComingTextSource from '@/components/domains/chat/chat-general/GeneralMessageChatComingTextSource.vue';
import GeneralMessageChatComingTextTarget from '@/components/domains/chat/chat-general/GeneralMessageChatComingTextTarget.vue';
import { useAudioStore } from '@/stores/audio';
import { ChatMessage } from '@/types/socket';

import GeneralMessageChatComingActions from '../chat-general/GeneralMessageChatComingActions.vue';



const props = withDefaults(
  defineProps<{
    message: ChatMessage;
    isLastIsOther: boolean;
    isTargetPlaying: boolean;
    isSourcePlaying: boolean;
    isVisibleSource: boolean;
    isVisibleTarget: boolean;
    usingAutoHeight?: boolean;
  }>(),
  {
    message: () => ({}) as ChatMessage,
    isLastIsOther: false,
    isSourcePlaying: false,
    isTargetPlaying: false,
    isVisibleSource: false,
    isVisibleTarget: false,
    usingAutoHeight: true,
  }
);

const audioStore = useAudioStore();
const { audioDownloading } = storeToRefs(audioStore);

const isTargetAudioPlaying = ref<boolean>(false);

const isDownloadingTarget = computed<boolean>(
  () =>
    audioDownloading.value === props.message.callLogId &&
    isTargetAudioPlaying.value
);

const isPendingSrc = computed(() => {
  return props.message.reset === '0' && !props.message.src.contents;
});

const isInterpretingTgt = computed(() => {
  return props.message.reset === '0' && !props.message.tgt.contents;
});

// 번역문 오디오 재생
const playTargetAudio = () => {
  if (isTargetAudioPlaying.value) {
    isTargetAudioPlaying.value = false;
    audioStore.setPlayState(0, props.message.callLogId);
    return;
  }
  if (props.message.tgtAudioKeys) {
    const { callLogId, tgtAudioKeys } = props.message;
    audioStore.setAudioManulSrcs(true, callLogId, tgtAudioKeys);
  }
};

//재생 ui표시
watch(
  () => [audioStore.playState, audioStore.playCallLogId],
  ([newVal1, newVal2]) => {
    if (newVal1 == 1) {
      isTargetAudioPlaying.value = false;
    } else if (newVal1 == 2) {
      isTargetAudioPlaying.value = newVal2 === props.message.callLogId;
    } else {
      isTargetAudioPlaying.value = false;
    }
  }
);
</script>

<style scoped lang="scss">
.other-chat-bubble {
  justify-content: flex-end;
  border-radius: 2px 12px 12px 12px;
  width: 100%;
  flex-shrink: 0;
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}

.other-chat-bubble__content {
  display: inline-flex;
  flex-direction: column;
  width: 100%;
}

.wrapper-divider {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 1rem;
}

.divider {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  width: 100%;
  flex: 1;
}

.actions {
  height: 1.75rem;
  width: 3.75rem;
}
</style>
